/**
 * Colorado purchase-delta math. Lifted from
 * `apps/customer-web/src/lib/cart-purchase-deltas.ts` so customer-mobile
 * shares the same axes (flower oz, concentrate g, THC mg) as the server's
 * `computeOrderPurchaseDeltas`.
 *
 * Canonicalization: real Metrc data uses category names like "Bud
 * Allocated for Extraction", "Pre-Rolls", "Live Resin Cartridge", etc.
 * The original exact-match `cat === "flower"` check missed all of those
 * — they fell into the else branch with no thcMgPerUnit and contributed
 * zero to the daily cap. Fix: normalize via `normalizeProductCategory`
 * (same helper that drives the customer-facing eyebrow + filter pills)
 * so legal classification matches displayed classification.
 */
import { COLORADO_DAILY_PURCHASE_MAX_OUNCE_EQUIVALENT, coloradoPurchaseOunceEquivalent, } from "@the-drop/types";
import { normalizeProductCategory } from "../normalize-product-category";
const GRAMS_PER_OUNCE = 28.349523125;
export function purchaseDeltasFromCartLines(lines) {
    let flowerOz = 0;
    let concentrateG = 0;
    let thcMg = 0;
    for (const li of lines) {
        const q = Number(li.quantity);
        if (!Number.isFinite(q) || q <= 0) {
            continue;
        }
        const canonical = normalizeProductCategory(li.productCategory, li.productName);
        const unit = li.quantityUnit.trim().toLowerCase();
        if (canonical === "Accessories") {
            // Non-cannabis merch (shirts, grinders, lighters) — contributes to no
            // purchase-limit axis.
            continue;
        }
        if (canonical === "Flower") {
            if (unit === "grams" || unit === "gram" || unit === "g") {
                flowerOz += q / GRAMS_PER_OUNCE;
            }
            else {
                flowerOz += q;
            }
        }
        else if (canonical === "Concentrate" || canonical === "Vapes") {
            // Vape carts are Retail Marijuana Concentrate; they count toward the
            // concentrate axis by grams (1 CCR 212-3 Rule 6-110(C) equivalency;
            // HB21-1317 8 g/day concentrate cap), never by THC mg.
            concentrateG += q;
        }
        else {
            // Edibles — count via thcMgPerUnit.
            const per = li.thcMgPerUnit != null ? Number(li.thcMgPerUnit) : NaN;
            if (Number.isFinite(per)) {
                thcMg += per * q;
            }
        }
    }
    return { flowerOz, concentrateG, thcMg };
}
const EQ_EPS = 1e-9;
/**
 * Colorado daily pool: `used + cart` must not exceed 1.0 ounce-equivalent
 * (`flowerOz + concentrateG/8 + thcMg/800`), matching server's
 * `checkPurchaseLimits` / `schema.sql`.
 */
export function combinedPurchaseExceedsDailyCap(cart, used = { flowerOz: 0, concentrateG: 0, thcMg: 0 }) {
    const usedEq = coloradoPurchaseOunceEquivalent(used);
    const cartEq = coloradoPurchaseOunceEquivalent(cart);
    return usedEq + cartEq > COLORADO_DAILY_PURCHASE_MAX_OUNCE_EQUIVALENT + EQ_EPS;
}
export function formatCombinedLimitExceededMessage(cart, used = { flowerOz: 0, concentrateG: 0, thcMg: 0 }) {
    const usedEq = coloradoPurchaseOunceEquivalent(used);
    const cartEq = coloradoPurchaseOunceEquivalent(cart);
    const total = usedEq + cartEq;
    return `Would exceed Colorado daily purchase limit (1 oz equivalent). This cart is about ${total.toFixed(4)} equivalent oz (flower + concentrate÷8 + THC÷800); max is ${COLORADO_DAILY_PURCHASE_MAX_OUNCE_EQUIVALENT}.`;
}
//# sourceMappingURL=purchase-deltas.js.map